import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { useEffect } from 'react';
import { Workspace, Organization, WorkspaceMember } from '@/types/workspace';

export const useWorkspaces = () => {
  const { user, verifySession } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  console.log('🚀 === USEWORKSPACES HOOK INIT ===');
  console.log('👤 User state:', { 
    id: user?.id, 
    email: user?.email, 
    authenticated: !!user 
  });
  console.log('===============================');

  const workspacesQuery = useQuery<(Workspace & { organization: Organization })[]>({
    queryKey: ['workspaces', user?.id],
    queryFn: async () => {
      console.log('🔄 === WORKSPACES QUERY EXECUTING ===');
      if (!user?.id) {
        console.error('❌ User not authenticated');
        throw new Error('User not authenticated');
      }

      // Verify session before making database calls
      console.log('🔍 Verifying session before workspace query...');
      const { isValid, needsRefresh } = await verifySession();
      
      if (!isValid) {
        console.error('❌ Invalid session - cannot query workspaces');
        throw new Error('Invalid session - please sign in again');
      }
      
      if (needsRefresh) {
        console.warn('⚠️ Session needs refresh before workspace query');
      }
      
      console.log('🔍 Fetching workspaces for user:', user.email, 'ID:', user.id);
      
      // Check if user is owner using database function
      console.log('🔒 Checking owner status for:', user.email);
      const { data: isOwnerResult } = await supabase.rpc('is_owner');
      const isOwner = isOwnerResult || false;
      console.log('🔒 Is owner:', isOwner);
      
      try {
        if (isOwner) {
          console.log('👑 Owner: Fetching ALL workspaces');
          // Owner can see all workspaces
          const { data, error } = await supabase
            .from('workspaces')
            .select(`
              *,
              organization:organizations(*)
            `)
            .order('is_default', { ascending: false });
          
          if (error) {
            console.error('❌ Owner workspace query error:', error);
            throw error;
          }
          
          console.log('✅ Owner workspaces loaded:', data?.length || 0, 'workspaces');
          return data as (Workspace & { organization: Organization })[];
        } else {
          console.log('👤 Regular user: Fetching member workspaces');
          // Regular users see only their workspaces
          const { data, error } = await supabase
            .from('workspaces')
            .select(`
              *,
              organization:organizations(*),
              workspace_members!inner(role, status)
            `)
            .eq('workspace_members.user_id', user.id)
            .eq('workspace_members.status', 'active')
            .order('is_default', { ascending: false });

          if (error) {
            console.error('❌ Regular user workspace query error:', error);
            throw error;
          }
          
          console.log('✅ Regular user workspaces loaded:', data?.length || 0, 'workspaces');
          return data as (Workspace & { organization: Organization })[];
        }
      } catch (err) {
        console.error('❌ Workspace fetch failed:', err);
        throw err;
      }
    },
    enabled: !!user?.id,
    retry: 1
  });

  console.log('📊 WORKSPACES QUERY STATE:', {
    isLoading: workspacesQuery.isLoading,
    isError: workspacesQuery.isError,
    isSuccess: workspacesQuery.isSuccess,
    enabled: !!user?.id,
    dataCount: workspacesQuery.data?.length || 0,
    error: workspacesQuery.error?.message
  });

  const defaultWorkspaceQuery = useQuery<string>({
    queryKey: ['default-workspace', user?.id],
    queryFn: async () => {
      console.log('🔄 === DEFAULT WORKSPACE QUERY EXECUTING ===');
      if (!user?.id) {
        console.error('❌ Default workspace: User not authenticated');
        throw new Error('User not authenticated');
      }

      // Verify session before making database calls
      console.log('🔍 Verifying session before default workspace query...');
      const { isValid, needsRefresh } = await verifySession();
      
      if (!isValid) {
        console.error('❌ Invalid session - cannot query default workspace');
        throw new Error('Invalid session - please sign in again');
      }
      
      if (needsRefresh) {
        console.warn('⚠️ Session needs refresh before default workspace query');
      }
      
      console.log('🔍 Fetching default workspace for user:', user.email);
      
      // Check if user is owner using database function
      const { data: isOwnerResult } = await supabase.rpc('is_owner');
      const isOwner = isOwnerResult || false;
      
      try {
        if (isOwner) {
          console.log('👑 Owner: Getting default workspace');
          // Owner gets first available workspace (preferably default)
          const { data, error } = await supabase
            .from('workspaces')
            .select('id')
            .order('is_default', { ascending: false })
            .limit(1)
            .single();
          
          if (error) {
            console.error('❌ Owner default workspace error:', error);
            throw error;
          }
          
          console.log('✅ Owner default workspace:', data?.id);
          return data?.id;
        } else {
          console.log('👤 Regular user: Getting default workspace');
          // Regular users get their default workspace
          const { data, error } = await supabase
            .from('workspace_members')
            .select(`
              workspace_id,
              workspaces!inner(*)
            `)
            .eq('user_id', user.id)
            .eq('status', 'active');

          if (error) {
            console.error('❌ Regular user default workspace error:', error);
            throw error;
          }
          
          // Sort client-side to get default workspace first
          const sortedData = (data || []).sort((a, b) => {
            const aIsDefault = (a as any).workspaces?.is_default || false;
            const bIsDefault = (b as any).workspaces?.is_default || false;
            return Number(bIsDefault) - Number(aIsDefault);
          });
          
          const defaultWorkspace = sortedData[0];
          console.log('✅ Regular user default workspace:', defaultWorkspace?.workspace_id);
          return defaultWorkspace?.workspace_id;
        }
      } catch (err) {
        console.error('❌ Default workspace fetch failed:', err);
        throw err;
      }
    },
    enabled: !!user?.id,
    retry: 1
  });

  console.log('📊 DEFAULT WORKSPACE QUERY STATE:', {
    isLoading: defaultWorkspaceQuery.isLoading,
    isError: defaultWorkspaceQuery.isError,
    isSuccess: defaultWorkspaceQuery.isSuccess,
    enabled: !!user?.id,
    defaultWorkspaceId: defaultWorkspaceQuery.data,
    error: defaultWorkspaceQuery.error?.message
  });

  // Handle errors with toast notifications
  useEffect(() => {
    if (workspacesQuery.isError && workspacesQuery.error) {
      console.error('❌ WORKSPACES QUERY ERROR:', workspacesQuery.error);
      toast({
        title: "Workspace Loading Error",
        description: "Failed to load workspaces. Please try refreshing the page.",
        variant: "destructive",
      });
    }
  }, [workspacesQuery.isError, workspacesQuery.error, toast]);

  useEffect(() => {
    if (defaultWorkspaceQuery.isError && defaultWorkspaceQuery.error) {
      console.error('❌ DEFAULT WORKSPACE QUERY ERROR:', defaultWorkspaceQuery.error);
      toast({
        title: "Default Workspace Error", 
        description: "Failed to load default workspace. Please select a workspace manually.",
        variant: "destructive",
      });
    }
  }, [defaultWorkspaceQuery.isError, defaultWorkspaceQuery.error, toast]);

  // Log successful queries
  useEffect(() => {
    if (workspacesQuery.isSuccess && workspacesQuery.data) {
      console.log('✅ WORKSPACES QUERY SUCCESS:', {
        count: workspacesQuery.data.length,
        workspaces: workspacesQuery.data.map(w => ({ id: w.id, name: w.name, isDefault: w.is_default }))
      });
    }
  }, [workspacesQuery.isSuccess, workspacesQuery.data]);

  useEffect(() => {
    if (defaultWorkspaceQuery.isSuccess && defaultWorkspaceQuery.data) {
      console.log('✅ DEFAULT WORKSPACE QUERY SUCCESS:', { defaultWorkspaceId: defaultWorkspaceQuery.data });
    }
  }, [defaultWorkspaceQuery.isSuccess, defaultWorkspaceQuery.data]);

  const result = {
    workspaces: (workspacesQuery.data || []) as (Workspace & { organization: Organization })[],
    defaultWorkspaceId: defaultWorkspaceQuery.data,
    isLoading: workspacesQuery.isLoading || defaultWorkspaceQuery.isLoading,
    error: workspacesQuery.error || defaultWorkspaceQuery.error,
  };

  console.log('🎯 === USEWORKSPACES HOOK RESULT ===');
  console.log('📦 Final result:', {
    workspacesCount: result.workspaces.length,
    defaultWorkspaceId: result.defaultWorkspaceId,
    isLoading: result.isLoading,
    hasError: !!result.error,
    errorMessage: result.error?.message
  });
  console.log('=================================');

  return result;
};

export const useWorkspaceMembers = (workspaceId: string) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const membersQuery = useQuery({
    queryKey: ['workspace-members', workspaceId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('workspace_members')
        .select('*')
        .eq('workspace_id', workspaceId)
        .eq('status', 'active');

      if (error) throw error;
      return data as WorkspaceMember[];
    },
    enabled: !!workspaceId,
  });

  const inviteMemberMutation = useMutation({
    mutationFn: async ({ email, role }: { email: string; role: string }) => {
      const { data, error } = await supabase
        .from('workspace_invitations')
        .insert({
          workspace_id: workspaceId,
          email,
          role_code: role,  // Use role_code for new schema
          role,  // Keep role for backward compatibility
          invited_by: (await supabase.auth.getUser()).data.user?.id || ''
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workspace-members', workspaceId] });
      toast({
        title: "Success",
        description: "Invitation sent successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to send invitation",
        variant: "destructive",
      });
    },
  });

  return {
    members: membersQuery.data || [],
    isLoading: membersQuery.isLoading,
    error: membersQuery.error,
    inviteMember: inviteMemberMutation.mutate,
    isInviting: inviteMemberMutation.isPending,
  };
};